<template>
  <div
    id="app"
    :class="{
      'invitation-bar': (isNotPublicRoute && !parametersNotCompleted),
      'example-bar': (isOnHomePage && !loggedIn),
      'fixed': isModalOpen || memorialLocked && $route.name === 'memorial.show'
    }"
  >
    <custom-notification :duration="5000" />
    <b-navbar fixed="top" class="nav-top">
      <b-navbar-toggle class="d-block d-md-none" target="nav_collapse" @click.native="onNavbarToggleClick" />
      <b-navbar-brand :to="{ name: 'home' }" class="logo" @click.native="scrollToTop + closeModal('sign-in-sign-up-modal')">
        <img :alt="$t('title')" src="/static/img/logo-home.png">
      </b-navbar-brand>
      <b-navbar-toggle
        v-if="isNotPublicRoute"
        class="d-block d-md-none sub-nav-toggle-button"
        target="sub_nav_collapse"
        @click.native="onSubNavbarToggleClick"
      />
      <b-navbar-nav :class="{ open: menuOpen }" class="ml-auto flex-row">
        <b-nav-item-dropdown class="languages">
          <template slot="button-content">
            <i class="ti ti-fw ti-lg ti-world" />
            <span class="nav-item-text d-md-none d-lg-inline">{{ $t('form.language') }}</span>
          </template>
          <b-dropdown-item
            v-for="lang in languages"
            :key="lang.code"
            :class="{ active: $translation.getLocale() === lang.code }"
            @click="$bus.$emit('language-changed', lang.code)"
          >
            {{ lang.front_name }}
          </b-dropdown-item>
        </b-nav-item-dropdown>
        <template v-if="oauth()">
          <b-nav-item v-if="user().memorial" @click="clickOneNotification">
            <i class="ti ti-bell ti-fw ti-lg" />
            <span class="nav-item-text d-md-none d-lg-inline">{{ $t('submenu.notifications') }}</span>
            <template v-if="hasNotifications">
              <div class="count-notif">
                {{ countNotif }}
              </div>
            </template>
          </b-nav-item>
          <b-nav-item
            v-if="user().memorial"
            :to="{ name: 'memorial.show', params: { id: user().memorial.id }}"
            @click="onNavbarToggleClick()"
          >
            <i class="ti ti-user-circle ti-fw ti-lg" />
            <span class="nav-item-text d-md-none d-lg-inline">{{ $t('manager_space') }}</span>
          </b-nav-item>
          <b-nav-item :to="{ name: 'parameter' }" @click="sendEvent + onNavbarToggleClick()">
            <i class="ti ti-cog ti-fw ti-lg" />
            <span class="nav-item-text d-md-none d-lg-inline">{{ $t('submenu.parameters') }}</span>
          </b-nav-item>
          <logout klass="text-center nav-link" icon="ti-fw ti-off" @click.native="onNavbarToggleClick" />
        </template>
        <template v-else>
          <b-nav-item @click="openModal('sign-in-sign-up-modal', 'sign-up') + onNavbarToggleClick()">
            {{ $t('pages.sign_up.title') }}
          </b-nav-item>
          <b-nav-item @click="openModal('sign-in-sign-up-modal', 'sign-in') + onNavbarToggleClick()">
            {{ $t('pages.sign_in.title') }}
          </b-nav-item>
        </template>
      </b-navbar-nav>
    </b-navbar>
    <template v-if="isNotPublicRoute">
      <sub-nav :menu-open="subMenuOpen" />
    </template>
    <main :class="{ connected: isNotPublicRoute }">
      <div v-if="parametersNotCompleted" class="notification-alert-danger">
        <b-alert show variant="danger">
          {{ $t('pages.parameter.complete_manager') }}
          <b-button :to="{ name: 'parameter' }" variant="link">{{ $t('pages.parameter.title') }}</b-button>
        </b-alert>
      </div>
      <div :class="homePageContainer">
        <router-view :key="$route.fullPath" />
      </div>
      <live-notifications v-if="user()" />
    </main>
    <memorial-model v-if="!user() && isOnHomePage" />
    <invitation-module v-if="isNotPublicRoute && !parametersNotCompleted" />
    <e-modal
      event-id="sign-in-sign-up-modal"
      class="sign-modal"
    >
      <sign-form :active="modalId" />
    </e-modal>
    <e-modal :title="$t('pages.cgv.title')" class="cgv-modal" event-id="cgv">
      <p v-html="$t('pages.cgv.text')" />
    </e-modal>
    <e-footer />
  </div>
</template>

<script>
  import '@triotech/vue-core/src/lib/plugins/_';
  import Logout from '@triotech/vue-core/src/components/Logout';
  import CustomNotification from '@triotech/vue-core/src/components/CustomNotification';
  import EModal from '@/components/EModal';
  import SignForm from '@/components/SignForm';
  import SubNav from '@/components/SubNav';
  import LiveNotifications from '@/components/LiveNotifications';
  import EFooter from '@/components/EFooter';
  import InvitationModule from '@/components/InvitationModule';
  import MemorialModel from '@/components/MemorialModel';

  export default {
    name: 'App',
    components: {
      CustomNotification,
      SignForm,
      EModal,
      SubNav,
      Logout,
      LiveNotifications,
      EFooter,
      InvitationModule,
      MemorialModel,
    },
    data() {
      return {
        // eslint-disable-next-line no-underscore-dangle
        uid: this._uid,
        modalId: '',
        countNotif: 0,
        languages: {},
        menuOpen: false,
        subMenuOpen: false,
        isModalOpen: false,
        memorialLocked: false,
      };
    },
    computed: {
      hasNotifications() {
        return this.countNotif > 0;
      },
      loggedIn() {
        return !_.isEmpty(this.get().oauth);
      },
      isNotPublicRoute() {
        const routeName = this.$route.name;
        const disconnectedRoutes = [
          'home',
          'memorial.index',
          'cgv',
          'contact',
          'faq',
          'partner',
          'password.request',
          'password.reset',
          'password.reset',
          'become_prescriber',
        ];
        let route = true;

        _.each(disconnectedRoutes, (disconnectedRoute) => {
          if (routeName === disconnectedRoute) {
            route = false;
          }
        });

        return route;
      },
      homePageContainer() {
        return this.$route.name !== 'home' ? 'container' : 'container-fluid';
      },
      isOnHomePage() {
        return this.$route.name === 'home';
      },
      parametersNotCompleted() {
        if (this.oauth()) {
          const paymentStep = (
            this.user().memorial
            && _.size(this.user().memorial.subscriptions) === 0
            && this.user().memorial.promo_code === null
          );
          const lolStep = (
            this.user().memorial
            && !this.user().memorial.currency
          );
          return !this.user().last_name || lolStep || paymentStep;
        }

        return false;
      },
      item() {
        let item;

        if (this.user() && this.get().memorial.id === this.user().memorial.id) {
          item = this.user().memorial;
        } else {
          item = this.get().memorial;
        }
        return item;
      },
    },
    watch: {
      $route: {
        deep: true,
        handler() {
          document.body.style.height = 'auto';
          document.body.style.overflow = 'auto';
          document.body.style.position = 'initial';
          document.body.style.width = '100%';
          this.$set(this, 'menuOpen', false);
          this.$set(this, 'subMenuOpen', false);
        },
      },
      subMenuOpen: {
        deep: true,
        handler(newValue) {
          if (!newValue) {
            this.$root.$emit('bv::toggle::collapse', 'sub_nav_collapse');
          }
        },
      },
    },
    mounted() {
      this.$bus.$on('t-event.notification.new', () => {
        this.countNotif = this.countNotif + 1;
      });
      this.$bus.$on('t-event.notification.loaded', (data) => {
        this.countNotif = data.count;
      });
      this.$bus.$on('t-event.notification.read', () => {
        if (this.countNotif > 0) {
          this.countNotif = this.countNotif - 1;
        }
      });
      this.$bus.$on('modal-is-open', () => {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      });
      this.$bus.$on('modal-is-close', () => {
        document.body.style.position = 'initial';
        document.body.style.width = 'auto';
      });
      this.getLanguages();
      this.$bus.$on('t-event.t-modal.sign-in-sign-up-modal.opened', () => {
        this.isModalOpen = true;
      });
      this.$bus.$on('t-event.t-modal.sign-in-sign-up-modal.closed', () => {
        this.isModalOpen = false;
      });
      this.$bus.$on('t-event.memorial.locked', (locked) => {
        this.memorialLocked = locked;
      });
    },
    destroyed() {
      this.$bus.$off(['modal-is-open', 'modal-is-close']);
    },
    methods: {
      sendEvent() {
        this.$bus.$emit('t-event.click-on-parameters');
      },
      clickOneNotification() {
        this.$bus.$emit('t-event.toggle-notification-panel');
      },
      modalName(action) {
        return `presciber-${action}-${this.uid}`;
      },
      openModal(id, data = null) {
        this.$bus.$emit(`t-event.t-modal.${id}.open`, data);
      },
      async getLanguages() {
        this.languages = await this.$ajax.get('api/public/translation/');
      },
      onNavbarToggleClick() {
        this.menuOpen = !this.menuOpen;
        if (this.subMenuOpen) {
          this.$set(this, 'subMenuOpen', false);
        }
      },
      onSubNavbarToggleClick() {
        this.subMenuOpen = !this.subMenuOpen;
        if (this.menuOpen) {
          this.$set(this, 'menuOpen', false);
        }
      },
      scrollToTop() {
        window.scrollTo(0, 0);
      },
      closeModal(id) {
        this.$bus.$emit(`t-event.t-modal.${id}.close`);
      },
    },
  };
</script>

<style lang="scss">
  @import '~@/scss/vars';
  @import '~@/scss/media';
  @import '~@triotech/vue-core/src/scss/compass';
  @import '~@triotech/vue-core/src/scss/bootstrap';
  @import 'compass/css3/transform';
  @import 'compass/css3/transition';

  html {
    height: 100%;
  }

  body {
    padding-top: $nav-height;
    overflow: auto;
    min-height: 100%;
  }

  .languages .dropdown-menu-left {
    max-height: 60vh;
    overflow: auto;
  }

  .navbar-nav .dropdown-menu {
    position: absolute !important;
  }

  .sub-nav-toggle-button {
    background-color: $primary !important;

    .navbar-toggler-icon {
      background-image: url("data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='rgba(255, 255, 255, 0.5)' stroke-width='2' stroke-linecap='round' stroke-miterlimit='10' d='M4 7h22M4 15h22M4 23h22'/%3E%3C/svg%3E") !important;
    }
  }

  .has-navigate {
    /deep/ .sweet-modal {
      max-width: 100%;

      .sweet-content {
        padding-left: 70px;
        padding-right: 70px;
      }
    }
  }

  a {
    &:hover,
    &:focus {
      text-decoration: none !important;
    }
  }

  #app {
    min-height: calc(100vh - 76px);
    position: relative;
    padding-bottom: $nav-height;

    &.fixed {
      position: fixed;
      width: 100%;
    }

    &.invitation-bar, &.example-bar {
      padding-bottom: calc(#{$nav-height} + 80px);

      footer {
        bottom: 80px;
      }
    }
  }

  .logo {
    img {
      position: absolute;
      height: 90px;
      top: 10px;
    }
  }

  .cgv-modal .modal-form {
    z-index: 10000;
  }

  main.connected {
    padding-top: 88px;
  }

  h2 {
    font-size: 2.5rem !important;
  }

  p {
    font-size: 18px;
  }

  .intro {
    margin-bottom: 30px;
  }

  .count-notif {
    position: absolute;
    background: $red;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-size: 12px;
    line-height: 20px;
    top: 0px;
    left: 0px;
    color: $white;
    text-align: center;
  }

  .red-text {
    color: $primary;
  }

  .vue-select-image__item {
    width: 25%;
    height: 100px;
  }

  .notification-alert-danger {
    position: relative;
    z-index: 100;

    .alert {
      margin-bottom: 0;
      background-color: $red;
      color: $white;
      border: 0;
      border-radius: 0;
      padding: 5px;
      text-align: center;

      a {
        border: 1px solid $white;
        padding: 10px !important;
        margin: 0;
        border-radius: 0;
        color: $white !important;
        font-size: 16px;
        vertical-align: initial;

        &:hover {
          background-color: darken($red, 5);
        }
      }
    }
  }

  .navbar-light {
    background-color: $white;
    height: $nav-height;

    .navbar-brand {
      color: $primary;
    }
    .nav-item {
      position: relative;
      font-size: .9rem;
    }
  }

  .nav-top {
    z-index: 99999 !important;

    .nav-item,
    .logout {
      margin-left: 20px;

      .nav-link {
        display: flex;
        align-items: center;

        i {
          margin-right: 5px;
        }
      }
    }

    .logout {
      font-size: .9rem;
    }
  }

  .sweet-modal {
    overflow: auto;

    &.is-visible {
      max-height: calc(100% - (#{$nav-height} * 2));
    }

    .sweet-title {
      h2 {
        font-size: 1.2rem !important;
      }
    }
    .sweet-content {
      text-align: left !important;

      h1 {
        margin-bottom: 3rem;
        font-weight: normal;
        font-size: 2rem;
      }

      .field-richTextEditor {
        #quill-container {
          max-height: 240px;
          overflow: auto;
        }
      }
    }
  }

  .multiselect__tags,
  .form-control {
    border-color: $light-grey;
  }

  .input-group {
    height: $input-height;

    input {
      height: 100%;
    }

    .input-group-append {
      height: 100%;
      button {
        height: 100%;
      }
    }
  }

  .sign-modal {
    .sweet-modal {
      background-color: $primary;
      height: 280px;
      overflow: visible !important;
      width: 80%;
      max-width: 900px;

      .sweet-content {
        .sweet-content-content {
          position: absolute;
          top: 50%;
          @include transform(translateY(-50%));
        }
      }

      .sweet-box-actions {
        position: absolute;
        top: 0;
        right: 0;

        .sweet-action-close {
          color: $white;
          background-color: $primary !important;
        }
      }
    }
  }

  .thumbnail {
    height: 100%;
    width: 100%;
    position: relative;
    text-align: center;
    color: $white;
    min-height: 100px;
    background: {
      size: cover;
      position: center;
      repeat: no-repeat;
    };

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba($black, .5);
      opacity: 0;
      z-index: 20;

      @include transition(opacity .2s);
    }

    &.clickable {
      cursor: pointer;

      &:hover {
        i {
          display: block;
        }

        &::before {
          opacity: 1;
        }
      }

      i {
        display: none;
        position: absolute;
        top: 35%;
        left: 0;
        right: 0;
        margin: auto;
        z-index: 20;
      }
    }
  }

  .sweet-modal-overlay {
    background: rgba($black, .5) !important;
  }

  .btn {
    white-space: normal;
  }

  .btn-primary {
    background-color: $primary !important;
    border-color: darken($primary, 10) !important;
    padding: 10px !important;

    &:hover {
      background-color: darken($primary, 10) !important;
    }
  }

  .btn-outline-primary {
    border-color: $primary !important;
    padding: 10px;
    color: $primary;

    &:hover {
      background-color: $primary !important;
    }
  }

  .btn-link {
    color: $primary !important;
    text-decoration: none !important;

    &:hover {
      text-decoration: none;
      color: darken($primary, 10);
    }
  }

  .title {
    margin: 2rem 0;
  }


  @include editable-max-width(1500px) {
    .sign-modal {
      .sweet-modal {
        width: 100%;
        max-width: 900px;
      }
    }
  }

  @include editable-max-width(991px) {
    .sign-modal {
      .sweet-modal {
        width: 100%;
        max-width: 100%;
        height: calc(100% - #{$nav-height});
        overflow: auto !important;

        .sweet-box-actions {
          top: 20px;
          right: 30px;
        }

        .sweet-content {
          padding: 10px 0 !important;
          overflow: auto;
        }
      }
    }

    .logout span {
      display: none;
    }
  }

  @include editable-max-width(767px) {
    main.connected {
      padding-top: 0;
    }

    .logo {
      img {
        position: initial;
        height: 50px;
        right: 10px;
      }
    }

    .logout span {
      display: block;
    }

    .nav-top {
      &.navbar {
        ul {
          display: none;

          &.open {
            display: block;
            width: 100%;
            position: absolute;
            top: $nav-height;
            background: $white;
            left: 0;
          }
        }
      }
    }

    .sign-modal {
      .sweet-modal {
        .sweet-content {
          .sweet-content-content {
            position: relative;
            @include transform(translateY(0));
          }
        }

        .sweet-box-actions {
          top: 15px;
          right: 20px;
          z-index: 999;

          .sweet-action-close {
            color: $primary;
          }
        }
      }
    }

    #app {
      &.invitation-bar {
        padding-bottom: calc(#{$nav-height} + 98px);

        footer {
          bottom: 98px;
        }
      }
    }
  }

  @include editable-max-width(600px) {
    .sweet-modal {
      &.is-mobile-fullscreen.is-visible {
        top: $nav-height;
      }
    }
  }

  @include editable-max-width(480px) {
    .sweet-modal {
      padding-bottom: 60px;

      &.is-visible {
        max-height: calc(100% - #{$nav-height});
      }
    }

    .navbar-light {
      padding: .4rem .5rem;

      a.navbar-brand {
        margin-right: 0.5rem;
      }
    }

    nav.sub-navbar {
      top: $nav-height;
    }

    .field-datePicker {
      .field-wrap {
        .vdp-datepicker__calendar {
          width: auto;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/App.vue